/**
 * @mention typeahead + NIP-05 resolve for OpenDork composer.
 */

import { nip19 } from 'nostr-tools';

import { getProfileCached } from '@/lib/profileCache';
import type { ChatMention } from '@/lib/workspace/types';
import { npubOf } from './packs';

export interface MentionCandidate {
  pubkey: string;
  npub: string;
  displayName: string;
  name?: string;
  nip05?: string;
  picture?: string;
  about?: string;
  /**
   * follow | cache | nip05 | self | npub | network
   * `network` = special @nostr token (search my follows), not a person.
   */
  source: 'follow' | 'cache' | 'nip05' | 'self' | 'npub' | 'network';
}

/** Sentinel pubkey for the @nostr network-search token (never a real key). */
export const NOSTR_NETWORK_SENTINEL = 'opendork-network-search';

/** Built-in typeahead row for @nostr / @network. */
export function networkSearchCandidate(): MentionCandidate {
  return {
    pubkey: NOSTR_NETWORK_SENTINEL,
    npub: 'nostr',
    displayName: 'nostr',
    name: 'network',
    about: 'Search recent notes from people you follow',
    source: 'network',
  };
}

export function isNetworkSearchCandidate(
  c: Pick<MentionCandidate, 'source' | 'pubkey' | 'displayName' | 'npub'>,
): boolean {
  return (
    c.source === 'network' ||
    c.pubkey === NOSTR_NETWORK_SENTINEL ||
    // Typeahead / picker sentinel uses literal "nostr" as npub (never a real bech32)
    c.npub === 'nostr'
  );
}

/**
 * People-only filter for the Nostr picker People tab.
 * Never injects the special @nostr network-search token (that lives on Quick
 * and in the @ typeahead). Use this so "nostr" never looks like a person row.
 */
export function filterPeopleOnlyCandidates(
  candidates: MentionCandidate[],
  query: string,
  limit = 12,
): MentionCandidate[] {
  const people = candidates.filter((c) => !isNetworkSearchCandidate(c));
  return filterMentionCandidatesPeople(people, query.trim().toLowerCase().replace(/^@/, ''), limit);
}

export interface FollowProfile {
  pubkey: string;
  displayName?: string;
  name?: string;
  nip05?: string;
  picture?: string;
  about?: string;
}

function displayOf(p: {
  displayName?: string;
  name?: string;
  nip05?: string;
  pubkey: string;
}): string {
  return (
    p.displayName?.trim() ||
    p.name?.trim() ||
    p.nip05?.split('@')[0] ||
    shortPub(p.pubkey)
  );
}

function shortPub(hex: string): string {
  return hex.length > 16 ? `${hex.slice(0, 8)}…${hex.slice(-4)}` : hex;
}

/** Parse follows list from a kind-3 contact list event tags. */
export function pubkeysFromContactList(tags: string[][]): string[] {
  const out: string[] = [];
  const seen = new Set<string>();
  for (const t of tags) {
    if (t[0] !== 'p' || !t[1]) continue;
    const pk = t[1].toLowerCase();
    if (!/^[0-9a-f]{64}$/.test(pk)) continue;
    if (seen.has(pk)) continue;
    seen.add(pk);
    out.push(pk);
  }
  return out;
}

/**
 * Build mention candidates from follows + profile cache + optional self.
 */
export function buildMentionIndex(opts: {
  follows: FollowProfile[];
  selfPubkey?: string;
  selfProfile?: FollowProfile | null;
}): MentionCandidate[] {
  const byPk = new Map<string, MentionCandidate>();

  const add = (c: MentionCandidate) => {
    const existing = byPk.get(c.pubkey);
    if (!existing) {
      byPk.set(c.pubkey, c);
      return;
    }
    // Prefer richer metadata
    byPk.set(c.pubkey, {
      ...existing,
      displayName:
        c.displayName !== shortPub(c.pubkey) ? c.displayName : existing.displayName,
      name: c.name || existing.name,
      nip05: c.nip05 || existing.nip05,
      picture: c.picture || existing.picture,
      about: c.about || existing.about,
      source: existing.source === 'self' ? 'self' : c.source,
    });
  };

  if (opts.selfPubkey) {
    const sp = opts.selfProfile;
    const cached = getProfileCached(opts.selfPubkey);
    const meta = cached?.metadata;
    add({
      pubkey: opts.selfPubkey,
      npub: npubOf(opts.selfPubkey),
      displayName: displayOf({
        pubkey: opts.selfPubkey,
        displayName:
          sp?.displayName ||
          (typeof meta?.display_name === 'string' ? meta.display_name : undefined),
        name: sp?.name || (typeof meta?.name === 'string' ? meta.name : undefined),
        nip05: sp?.nip05 || (typeof meta?.nip05 === 'string' ? meta.nip05 : undefined),
      }),
      name: sp?.name || (typeof meta?.name === 'string' ? meta.name : undefined),
      nip05: sp?.nip05 || (typeof meta?.nip05 === 'string' ? meta.nip05 : undefined),
      picture:
        sp?.picture || (typeof meta?.picture === 'string' ? meta.picture : undefined),
      about: sp?.about || (typeof meta?.about === 'string' ? meta.about : undefined),
      source: 'self',
    });
  }

  for (const f of opts.follows) {
    const cached = getProfileCached(f.pubkey);
    const meta = cached?.metadata;
    add({
      pubkey: f.pubkey,
      npub: npubOf(f.pubkey),
      displayName: displayOf({
        pubkey: f.pubkey,
        displayName:
          f.displayName ||
          (typeof meta?.display_name === 'string' ? meta.display_name : undefined),
        name: f.name || (typeof meta?.name === 'string' ? meta.name : undefined),
        nip05: f.nip05 || (typeof meta?.nip05 === 'string' ? meta.nip05 : undefined),
      }),
      name: f.name || (typeof meta?.name === 'string' ? meta.name : undefined),
      nip05: f.nip05 || (typeof meta?.nip05 === 'string' ? meta.nip05 : undefined),
      picture:
        f.picture || (typeof meta?.picture === 'string' ? meta.picture : undefined),
      about: f.about || (typeof meta?.about === 'string' ? meta.about : undefined),
      source: 'follow',
    });
  }

  return [...byPk.values()];
}

/** Filter candidates by typeahead query (name, nip05, npub, hex prefix). */
export function filterMentionCandidates(
  candidates: MentionCandidate[],
  query: string,
  limit = 8,
): MentionCandidate[] {
  const q = query.trim().toLowerCase().replace(/^@/, '');
  // Empty query after @: show network first, then people
  if (!q) {
    const people = candidates.filter((c) => c.source !== 'network').slice(0, limit - 1);
    return [networkSearchCandidate(), ...people].slice(0, limit);
  }

  // @nostr / @network / @follows
  if (
    'nostr'.startsWith(q) ||
    'network'.startsWith(q) ||
    'follows'.startsWith(q) ||
    q === 'wot' ||
    'feed'.startsWith(q)
  ) {
    const rest = filterMentionCandidatesPeople(candidates, q, limit - 1);
    return [networkSearchCandidate(), ...rest].slice(0, limit);
  }

  return filterMentionCandidatesPeople(candidates, q, limit);
}

/** Exported for People-tab filtering (no network token injection). */
export function filterMentionCandidatesPeople(
  candidates: MentionCandidate[],
  q: string,
  limit: number,
): MentionCandidate[] {
  if (!q) {
    return candidates.filter((c) => c.source !== 'network').slice(0, limit);
  }

  // Direct npub / hex
  if (q.startsWith('npub1') || q.startsWith('nprofile1')) {
    try {
      const decoded = nip19.decode(q);
      if (decoded.type === 'npub') {
        const pubkey = decoded.data as string;
        return [
          {
            pubkey,
            npub: npubOf(pubkey),
            displayName: shortPub(pubkey),
            source: 'npub',
          },
        ];
      }
      if (decoded.type === 'nprofile') {
        const pubkey = (decoded.data as { pubkey: string }).pubkey;
        return [
          {
            pubkey,
            npub: npubOf(pubkey),
            displayName: shortPub(pubkey),
            source: 'npub',
          },
        ];
      }
    } catch {
      // fall through
    }
  }
  if (/^[0-9a-f]{8,64}$/i.test(q)) {
    const hex = q.toLowerCase();
    const hits = candidates.filter((c) => c.pubkey.startsWith(hex));
    if (hits.length) return hits.slice(0, limit);
    if (hex.length === 64) {
      return [
        {
          pubkey: hex,
          npub: npubOf(hex),
          displayName: shortPub(hex),
          source: 'npub',
        },
      ];
    }
  }

  const scored = candidates
    .map((c) => {
      const name = c.displayName.toLowerCase();
      const handle = (c.name || '').toLowerCase();
      const nip = (c.nip05 || '').toLowerCase();
      const nipLocal = nip.split('@')[0] || '';
      let score = 0;
      if (name === q || handle === q || nipLocal === q || nip === q) score = 100;
      else if (name.startsWith(q) || handle.startsWith(q) || nipLocal.startsWith(q))
        score = 80;
      else if (nip.startsWith(q)) score = 70;
      else if (name.includes(q) || handle.includes(q) || nip.includes(q)) score = 40;
      else if (c.npub.toLowerCase().includes(q)) score = 20;
      if (c.source === 'self' && (q === 'me' || q === 'self')) score = 200;
      return { c, score };
    })
    .filter((x) => x.score > 0)
    .sort((a, b) => b.score - a.score || a.c.displayName.localeCompare(b.c.displayName));

  return scored.slice(0, limit).map((x) => x.c);
}

/** Detect @query at caret for typeahead. */
export function getMentionQueryAtCaret(
  text: string,
  caret: number,
): { start: number; query: string } | null {
  const before = text.slice(0, caret);
  // Match @token at end (no spaces in query for typeahead)
  const m = before.match(/(^|[\s([{"'])@([^\s@]*)$/);
  if (!m) return null;
  const atIdx = before.lastIndexOf('@');
  if (atIdx < 0) return null;
  const query = before.slice(atIdx + 1);
  // Don't trigger if it looks like an email mid-word without boundary — already handled
  return { start: atIdx, query };
}

/** Replace @query with @DisplayName and return new text + mention. */
export function applyMentionToText(
  text: string,
  caret: number,
  candidate: MentionCandidate,
): { text: string; caret: number; mention: ChatMention } {
  const q = getMentionQueryAtCaret(text, caret);
  const mention: ChatMention = {
    id: crypto.randomUUID(),
    pubkey: candidate.pubkey,
    npub: candidate.npub,
    displayName: candidate.displayName,
    nip05: candidate.nip05,
    picture: candidate.picture,
    fetchNotes: true,
  };

  if (!q) {
    const insert = `@${candidate.displayName} `;
    const next = text.slice(0, caret) + insert + text.slice(caret);
    return { text: next, caret: caret + insert.length, mention };
  }

  const insert = `@${candidate.displayName} `;
  const next = text.slice(0, q.start) + insert + text.slice(caret);
  return { text: next, caret: q.start + insert.length, mention };
}

/** Extract @DisplayName tokens that match known mentions (for send hygiene). */
export function syncMentionsWithText(
  text: string,
  mentions: ChatMention[],
): ChatMention[] {
  if (!mentions.length) return [];
  return mentions.filter((m) => {
    const token = `@${m.displayName}`;
    return text.includes(token) || text.includes(m.npub) || text.includes(m.pubkey);
  });
}

/**
 * Resolve NIP-05 name@domain → pubkey via .well-known/nostr.json.
 * Tries a direct fetch first (most well-known endpoints are CORS-open);
 * the CORS proxy template is only a fallback, so lookups don't leak to a
 * proxy unless the domain actually requires it.
 */
export async function resolveNip05(
  address: string,
  options?: { signal?: AbortSignal; corsProxyTemplate?: string },
): Promise<{ pubkey: string; relays?: string[] } | null> {
  const raw = address.trim().replace(/^@/, '');
  const at = raw.lastIndexOf('@');
  if (at <= 0) return null;
  const name = raw.slice(0, at).toLowerCase();
  const domain = raw.slice(at + 1).toLowerCase();
  if (!name || !domain || domain.includes('/')) return null;

  const url = `https://${domain}/.well-known/nostr.json?name=${encodeURIComponent(name)}`;

  const attempt = async (
    fetchUrl: string,
  ): Promise<{ pubkey: string; relays?: string[] } | null> => {
    try {
      const res = await fetch(fetchUrl, {
        signal: options?.signal ?? AbortSignal.timeout(6_000),
        headers: { Accept: 'application/json' },
      });
      if (!res.ok) return null;
      const data = (await res.json()) as {
        names?: Record<string, string>;
        relays?: Record<string, string[]>;
      };
      const pubkey = data.names?.[name] || data.names?.[raw.slice(0, at)];
      if (!pubkey || !/^[0-9a-f]{64}$/i.test(pubkey)) return null;
      const pk = pubkey.toLowerCase();
      return { pubkey: pk, relays: data.relays?.[pk] };
    } catch {
      return null;
    }
  };

  const direct = await attempt(url);
  if (direct) return direct;

  if (options?.corsProxyTemplate) {
    return attempt(
      options.corsProxyTemplate.replace('{href}', encodeURIComponent(url)),
    );
  }
  return null;
}

/** Memoized verification results (nip05|pubkey → bool). */
const nip05VerifyCache = new Map<string, boolean>();

/**
 * Verify that a declared NIP-05 address forward-resolves to the given
 * pubkey. Result is cached in-memory for the session.
 */
export async function verifyNip05ForPubkey(
  nip05: string,
  pubkey: string,
  options?: { signal?: AbortSignal; corsProxyTemplate?: string },
): Promise<boolean> {
  const key = `${nip05.trim().toLowerCase()}|${pubkey.toLowerCase()}`;
  const cached = nip05VerifyCache.get(key);
  if (cached !== undefined) return cached;
  const hit = await resolveNip05(nip05, {
    signal: options?.signal,
    corsProxyTemplate:
      options?.corsProxyTemplate ?? 'https://proxy.shakespeare.diy/?url={href}',
  });
  const ok = !!hit && hit.pubkey.toLowerCase() === pubkey.toLowerCase();
  nip05VerifyCache.set(key, ok);
  return ok;
}

/** True if string looks like name@domain */
export function looksLikeNip05(s: string): boolean {
  const t = s.trim().replace(/^@/, '');
  return /^[a-z0-9._-]+@[a-z0-9.-]+\.[a-z]{2,}$/i.test(t);
}

export function candidateToMention(c: MentionCandidate): ChatMention {
  return {
    id: crypto.randomUUID(),
    pubkey: c.pubkey,
    npub: c.npub,
    displayName: c.displayName,
    nip05: c.nip05,
    picture: c.picture,
    fetchNotes: true,
  };
}
